import React, { memo } from 'react';

import { Box, Typography } from '@mui/material';
import { ErrorFilledIcon, SuccessFilledIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { formatCurrencyValue } from 'utils/format';

import { ReadinessStepTestIds } from '../../ReadinessStep.testIds';

import {
  calloutIconStyles,
  cardStyles,
  getStatusCalloutStyles,
  rowLabelStyles,
  rowsStyles,
  rowStyles,
  titleStyles,
} from './LoanSnapshotCard.styles';

const EMPTY_VALUE = '—';

const formatRate = rate => {
  if (rate == null || rate === '') {
    return EMPTY_VALUE;
  }

  const parsed = Number(rate);

  return Number.isFinite(parsed) && parsed > 0 ? `${parsed}%` : EMPTY_VALUE;
};

const LoanSnapshotCard = ({ snapshot, summary }) => {
  const { packageName, loanAmount, rate, signerCount, creditor } = snapshot;
  const { blockingCount, isReadyToContinue } = summary;

  const rows = [
    { label: 'Package', value: packageName ?? EMPTY_VALUE },
    { label: 'Loan amount', value: formatCurrencyValue(loanAmount) ?? EMPTY_VALUE },
    { label: 'Rate', value: formatRate(rate) },
    { label: 'Signers', value: signerCount ? `${signerCount} · e-sign` : EMPTY_VALUE },
    { label: 'Creditor', value: creditor ?? EMPTY_VALUE },
  ];

  return (
    <Box data-testid={ReadinessStepTestIds.SNAPSHOT} sx={cardStyles}>
      <Typography sx={titleStyles} variant="bold12">
        Loan Snapshot
      </Typography>
      <Box sx={rowsStyles}>
        {rows.map(({ label, value }) => (
          <Box key={label} sx={rowStyles}>
            <Typography sx={rowLabelStyles} variant="regular12">
              {label}
            </Typography>
            <Typography variant="bold12">{value}</Typography>
          </Box>
        ))}
      </Box>
      <Box
        data-testid={ReadinessStepTestIds.CONTINUE_CALLOUT}
        sx={getStatusCalloutStyles(isReadyToContinue)}
      >
        <Box sx={calloutIconStyles}>
          {isReadyToContinue ? <SuccessFilledIcon size={16} /> : <ErrorFilledIcon size={16} />}
        </Box>
        <Typography variant="bold12">
          {isReadyToContinue
            ? 'All checks resolved. You can continue.'
            : `Resolve the ${blockingCount} red check${
                blockingCount === 1 ? '' : 's'
              } above to continue.`}
        </Typography>
      </Box>
    </Box>
  );
};

LoanSnapshotCard.propTypes = {
  snapshot: PropTypes.shape({
    packageName: PropTypes.string,
    loanAmount: PropTypes.number,
    rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    signerCount: PropTypes.number,
    creditor: PropTypes.string,
  }).isRequired,
  summary: PropTypes.shape({
    blockingCount: PropTypes.number.isRequired,
    isReadyToContinue: PropTypes.bool.isRequired,
  }).isRequired,
};

export default memo(LoanSnapshotCard);
